import {useMemo} from 'react';
import {Body2} from '@ui/atoms/typography';
import {FiChevronDown} from 'react-icons/fi';
import {useDropDown} from '@hooks';
import {SearchBox2} from '@ui/molecules/search-box';
import {DropdownItem} from '@pages/dashboard/you/you-page.styles';
import {
  ICheckinCycle,
  ICheckinCycleConfig,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {CustomColorIcon} from '@ui/atoms/color-icons';
import dayjs from 'dayjs';

type CycleWithConfig = ICheckinCycle & {config?: Partial<ICheckinCycleConfig>};

interface CycleSelectorDropdownProps {
  cycles: CycleWithConfig[];
  selectedCycleName: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onCycleSelect: (cycleId: string) => void;
  getCycleSecondaryLabel?: (cycle: CycleWithConfig) => string;
  showStatusDots?: boolean;
  fullWidth?: boolean;
}

export const CycleSelectorDropdown = ({
  cycles,
  selectedCycleName,
  searchValue,
  onSearchChange,
  onCycleSelect,
  getCycleSecondaryLabel,
  showStatusDots = true,
  fullWidth = false,
}: CycleSelectorDropdownProps) => {
  const {handleOpen, ref, handleClose, open} = useDropDown();

  const filteredCycles = useMemo(() => {
    if (!searchValue.trim()) return cycles;
    const searchTerm = searchValue.toLowerCase();
    return cycles.filter((cycle) =>
      cycle?.cycleName?.toLowerCase().includes(searchTerm),
    );
  }, [cycles, searchValue]);

  const getActiveScheduleLabel = (cycle: CycleWithConfig): string => {
    const frequency = cycle?.config?.frequency?.toLowerCase();
    const days = cycle?.config?.dayOfWeek;
    const dayOfMonth = cycle?.config?.dayOfMonth;

    if (frequency === 'weekly') {
      if (Array.isArray(days) && days.length) {
        return `Weekly on ${days.join(', ')}`;
      }
      return 'Weekly';
    }

    if (frequency === 'daily') {
      return 'Daily';
    }

    if (frequency === 'monthly') {
      if (dayOfMonth) return `Monthly on ${dayOfMonth}`;
      return 'Monthly';
    }

    return 'Active';
  };

  const getCycleStatusLabel = (cycle: CycleWithConfig): string => {
    const status = cycle?.status?.toLowerCase();
    const isActive = status === 'active';
    const isArchived = status === 'archived' || status === 'ended';

    if (isArchived) {
      return `Archived on ${dayjs(cycle?.updatedAt || cycle?.createdAt).format(
        'MMM D',
      )}`;
    }

    if (isActive) {
      return getActiveScheduleLabel(cycle);
    }

    return `Ended on ${dayjs(cycle?.updatedAt || cycle?.createdAt).format(
      'MMM D',
    )}`;
  };

  const getCycleStatusColor = (cycle: CycleWithConfig): string => {
    const status = cycle?.status?.toLowerCase();
    if (status === 'active') return '#47B881';
    return '#BFBFD4';
  };

  const handleCycleSelect = (cycleId: string) => {
    onCycleSelect(cycleId);
    handleClose();
  };

  return (
    <div ref={ref} className={`relative ${fullWidth ? 'w-full' : ''}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e: any) => handleOpen(e)}
        className={`flex items-center gap-3 rounded-[10px] border border-borderDark px-4 py-3 cursor-pointer bg-white text-left ${
          fullWidth ? 'w-full justify-between' : ''
        }`}>
        <span className="flex min-w-0 items-center gap-3">
          {showStatusDots && (
            <span className="w-2 h-2 rounded-full bg-green-400 block" />
          )}
          <Body2 weight="semibold" kind="textDark" className="truncate">
            {selectedCycleName}
          </Body2>
        </span>
        <FiChevronDown className="ml-1" />
      </button>
      {open && (
        <div
          data-testid="cycle-selector-menu"
          className={`absolute top-full left-0 mt-2 border border-borderLight rounded-[10px] bg-white shadow-lg z-10 p-2 ${
            fullWidth ? 'w-full' : 'w-80'
          }`}>
          <SearchBox2
            placeholder="Search check-in cycles"
            value={searchValue}
            inputStyle={{height: '40px', fontWeight: 400}}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onSearchChange(e.target.value)
            }
          />
          <div className="overflow-y-auto max-h-[232px] mt-4" role="listbox">
            {filteredCycles.length > 0 ? (
              filteredCycles.map((cycle) => {
                const statusLabel = getCycleSecondaryLabel
                  ? getCycleSecondaryLabel(cycle)
                  : getCycleStatusLabel(cycle);
                const statusColor = getCycleStatusColor(cycle);

                return (
                  <DropdownItem
                    key={cycle.id}
                    onClick={() => handleCycleSelect(cycle.id)}>
                    <div className="flex flex-col">
                      <Body2 kind="textBody">
                        <span className="flex items-center gap-2">
                          {cycle.cycleName || 'Unnamed cycle'}
                          {showStatusDots && (
                            <CustomColorIcon
                              color={statusColor}
                              width="10px"
                              height="10px"
                              margin={0}
                            />
                          )}
                        </span>
                      </Body2>

                      <Body2
                        kind="textBody"
                        weight="semibold"
                        style={{fontSize: '10px'}}
                        className="text-[10px] mt-1">
                        {statusLabel}
                      </Body2>
                    </div>
                  </DropdownItem>
                );
              })
            ) : (
              <Body2 kind="textBody" className="px-4 py-2">
                No cycles found
              </Body2>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
